import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  useExpenses,
  useExpenseSummary,
  useDeleteExpense,
  useBulkDeleteExpenses,
  usePayExpense,
  useExpenseVoucher,
  useAssetCategories,
} from '../../../services/httpServices/queries';
import { useDebounce } from '../../../hooks/useDebounce';
import { Pagination } from '../../../components/ui/Pagination';
import { ConfirmDialog } from '../../../components/ui/confirm-dialog';
import { DatePicker } from '../../../components/ui/DatePicker';
import { ExpensesLayout } from './ExpensesLayout';
import { ExpenseDetailsModal } from './ExpenseDetailsModal';
import { toast } from 'sonner';
import { printPdfBlobUrl } from '../../../utils/printDocument';
import {
  Plus,
  Search,
  X,
  Loader2,
  AlertCircle,
  Pencil,
  Trash2,
  Printer,
  Download,
  CheckCircle,
  Clock,
  Wallet,
  RefreshCw,
  Receipt,
  Eye,
  SlidersHorizontal,
} from 'lucide-react';
import type { Expense, ExpenseFilters } from '../../../types/expense';

const CURRENCY = '৳';
const limit = 20;

function fmt(value: number | undefined | null): string {
  return `${CURRENCY}${(Number(value) || 0).toLocaleString('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  })}`;
}

function fmtDate(value?: string | null): string {
  if (!value) return '—';
  const date = new Date(value);
  if (isNaN(date.getTime())) return '—';
  return date.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

const PAYMENT_METHODS = [
  { value: 'cash', label: 'Cash' },
  { value: 'card', label: 'Card' },
  { value: 'upi', label: 'UPI' },
  { value: 'bank_transfer', label: 'Bank Transfer' },
  { value: 'insurance', label: 'Insurance' },
];

const FREQUENCY_LABELS: Record<string, string> = {
  DAILY: 'Daily',
  WEEKLY: 'Weekly',
  MONTHLY: 'Monthly',
  QUARTERLY: 'Quarterly',
  YEARLY: 'Yearly',
};

export function Expenses() {
  const navigate = useNavigate();
  const location = useLocation();
  const params = useMemo(() => new URLSearchParams(location.search), [location.search]);

  const [page, setPage] = useState(() => {
    const p = params.get('page');
    return p ? Math.max(1, parseInt(p, 10)) : 1;
  });
  const [search, setSearch] = useState(() => params.get('search') || '');
  const debouncedSearch = useDebounce(search, 350);
  const [categoryId, setCategoryId] = useState(() => params.get('categoryId') || '');
  const [status, setStatus] = useState(() => params.get('status') || '');
  const [expenseType, setExpenseType] = useState(() => params.get('expenseType') || '');
  const [dateFrom, setDateFrom] = useState(() => params.get('dateFrom') || '');
  const [dateTo, setDateTo] = useState(() => params.get('dateTo') || '');
  const [showFilters, setShowFilters] = useState(false);

  const [confirm, setConfirm] = useState<Expense | null>(null);
  const [paying, setPaying] = useState<Expense | null>(null);
  const [payForm, setPayForm] = useState({ paymentMethod: 'cash', paymentDate: '', paymentReference: '' });
  const [payError, setPayError] = useState<string | null>(null);
  const [viewing, setViewing] = useState<Expense | null>(null);

  // Row selection for bulk actions
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bulkConfirmOpen, setBulkConfirmOpen] = useState(false);
  const headerCheckboxRef = useRef<HTMLInputElement>(null);
  // Guards the filter-change page reset so it is skipped on first mount.
  const didMountRef = useRef(false);

  const filters: ExpenseFilters = {
    search: debouncedSearch || undefined,
    categoryId: categoryId || undefined,
    status: status || undefined,
    expenseType: expenseType || undefined,
    dateFrom: dateFrom || undefined,
    dateTo: dateTo || undefined,
    page,
    limit,
  };

  const { data, isLoading, error } = useExpenses(filters);
  const { data: summaryData } = useExpenseSummary(filters);
  const { data: categoriesData } = useAssetCategories(undefined, true, 'EXPENSE');

  const expenses = useMemo(() => data?.data || [], [data]);
  const meta = data?.meta;
  const summary = summaryData?.data;
  const categories = useMemo(() => categoriesData?.data || [], [categoriesData]);

  const deleteExpense = useDeleteExpense();
  const payExpense = usePayExpense();
  const voucher = useExpenseVoucher();
  const bulkDeleteExpenses = useBulkDeleteExpenses();

  const hasFilters = search || categoryId || status || expenseType || dateFrom || dateTo;
  const activeFilterCount =
    [categoryId, expenseType, status, dateFrom, dateTo].filter(Boolean).length + (search ? 1 : 0);

  // Mirror the active filters into the URL so they are shareable and survive a
  // refresh, matching the diagnostic-receipts list behaviour.
  useEffect(() => {
    const newParams = new URLSearchParams();
    if (page > 1) newParams.set('page', String(page));
    if (debouncedSearch) newParams.set('search', debouncedSearch);
    if (categoryId) newParams.set('categoryId', categoryId);
    if (status) newParams.set('status', status);
    if (expenseType) newParams.set('expenseType', expenseType);
    if (dateFrom) newParams.set('dateFrom', dateFrom);
    if (dateTo) newParams.set('dateTo', dateTo);
    const query = newParams.toString();
    navigate(`${location.pathname}${query ? `?${query}` : ''}`, { replace: true });
  }, [page, debouncedSearch, categoryId, status, expenseType, dateFrom, dateTo, navigate, location.pathname]);

  // Reset to the first page whenever a filter changes. Skipped on mount so a
  // deep link such as ?page=3 is honoured instead of being reset to page 1.
  useEffect(() => {
    if (!didMountRef.current) {
      didMountRef.current = true;
      return;
    }
    setPage(1);
  }, [search, categoryId, status, expenseType, dateFrom, dateTo]);

  useEffect(() => {
    if (!isLoading && expenses.length === 0 && page > 1) {
      setPage((prev) => Math.max(1, prev - 1));
    }
  }, [isLoading, expenses.length, page]);

  const allSelected = expenses.length > 0 && expenses.every((expense) => selectedIds.has(expense.id));
  const someSelected = expenses.some((expense) => selectedIds.has(expense.id));

  // Drop selected ids that are no longer visible (e.g. after paging/filtering/deleting).
  useEffect(() => {
    setSelectedIds((prev) => {
      if (prev.size === 0) return prev;
      const visible = new Set(expenses.map((expense) => expense.id));
      const next = new Set<string>();
      let changed = false;
      prev.forEach((id) => {
        if (visible.has(id)) next.add(id);
        else changed = true;
      });
      return changed ? next : prev;
    });
  }, [expenses]);

  useEffect(() => {
    if (headerCheckboxRef.current) {
      headerCheckboxRef.current.indeterminate = someSelected && !allSelected;
    }
  }, [someSelected, allSelected]);

  const clearFilters = () => {
    setSearch('');
    setCategoryId('');
    setStatus('');
    setExpenseType('');
    setDateFrom('');
    setDateTo('');
  };

  const getVoucherUrl = (id: string) => voucher.mutateAsync(id);

  const handlePrint = async (expense: Expense) => {
    try {
      const url = await getVoucherUrl(expense.id);
      await printPdfBlobUrl(url);
    } catch {
      toast.error('Failed to generate voucher');
    }
  };

  const handleDownload = async (expense: Expense) => {
    try {
      const url = await getVoucherUrl(expense.id);
      const link = document.createElement('a');
      link.href = url;
      link.download = `expense-voucher-${expense.expenseNumber}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
      toast.success('Voucher downloaded');
    } catch {
      toast.error('Failed to download voucher');
    }
  };

  const openPay = (expense: Expense) => {
    setPayError(null);
    setPayForm({
      paymentMethod: expense.paymentMethod || 'cash',
      paymentDate: new Date().toISOString().slice(0, 10),
      paymentReference: expense.paymentReference || '',
    });
    setPaying(expense);
  };

  const handlePay = () => {
    if (!paying) return;
    setPayError(null);
    if (!payForm.paymentDate) {
      setPayError('Please choose the payment date');
      return;
    }
    payExpense.mutate(
      {
        id: paying.id,
        data: {
          paymentMethod: payForm.paymentMethod,
          paymentDate: payForm.paymentDate,
          paymentReference: payForm.paymentReference || undefined,
        },
      },
      {
        onSuccess: () => setPaying(null),
        onError: (err: any) => setPayError(err?.message || 'Failed to mark expense as paid'),
      },
    );
  };

  const handleDelete = async () => {
    if (!confirm) return;
    try {
      await deleteExpense.mutateAsync(confirm.id);
    } catch {
      // toast handled by the mutation meta
    } finally {
      setConfirm(null);
    }
  };

  const toggleAll = () => {
    setSelectedIds((prev) => {
      const everySelected = expenses.length > 0 && expenses.every((expense) => prev.has(expense.id));
      return everySelected ? new Set() : new Set(expenses.map((expense) => expense.id));
    });
  };

  const toggleOne = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const openEdit = (expense: Expense) => {
    setViewing(null);
    navigate(`/billing/expenses/${expense.id}/edit`);
  };

  const handleConfirmBulkDelete = async () => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;
    try {
      const res = await bulkDeleteExpenses.mutateAsync(ids);
      const deletedCount = (res as any)?.data?.deleted?.length ?? ids.length;
      setSelectedIds(new Set());
      toast.success(`Deleted ${deletedCount} expense${deletedCount === 1 ? '' : 's'}`);
    } catch {
      toast.error('Failed to delete selected expenses');
    } finally {
      setBulkConfirmOpen(false);
    }
  };

  const summaryCards = [
    { label: 'Total Expenses', value: fmt(summary?.totalAmount), sub: `${summary?.count || 0} record(s)`, icon: Wallet, color: 'bg-primary/10 text-primary' },
    { label: 'Paid', value: fmt(summary?.paidAmount), sub: 'Settled', icon: CheckCircle, color: 'bg-success/10 text-success' },
    { label: 'Pending', value: fmt(summary?.pendingAmount), sub: 'Awaiting payment', icon: Clock, color: 'bg-warning/10 text-warning' },
    { label: 'Recurring', value: String(summary?.recurringCount || 0), sub: 'Fixed / repeat items', icon: RefreshCw, color: 'bg-primary/10 text-primary' },
  ];

  return (
    <ExpensesLayout
      title="Operating Expenses"
      description="Daily running costs and fixed/recurring expenses"
      helpKey="/billing/expenses"
      action={
        <Link
          to="/billing/expenses/new"
          className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors text-sm font-medium flex items-center gap-2"
        >
          <Plus size={16} />
          Record Expense
        </Link>
      }
    >
      {/* Summary */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {summaryCards.map((card) => (
          <div key={card.label} className="bg-surface rounded-xl border border-border p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-text-muted uppercase">{card.label}</p>
                <p className="text-xl font-bold text-text mt-1">{card.value}</p>
                <p className="text-[0.6875rem] text-text-muted mt-1">{card.sub}</p>
              </div>
              <div className={`p-2.5 rounded-lg ${card.color}`}>
                <card.icon size={18} />
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Search & Filters */}
      <div className="bg-surface rounded-xl border border-border p-4 space-y-3">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-muted" />
            <input
              type="text"
              placeholder="Search title, voucher no, payee..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-9 pr-4 py-2 bg-background border border-border rounded-lg text-sm text-text placeholder:text-text-muted focus:outline-none focus:ring-2 focus:ring-primary/50"
            />
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowFilters((v) => !v)}
              className={`px-3 py-2 border rounded-lg transition-colors text-sm flex items-center gap-1.5 whitespace-nowrap ${
                showFilters || hasFilters
                  ? 'bg-primary/10 border-primary text-primary'
                  : 'text-text-muted hover:text-text border-border'
              }`}
            >
              <SlidersHorizontal size={14} />
              Filters
              {activeFilterCount > 0 && (
                <span className="ml-0.5 w-5 h-5 rounded-full bg-primary text-white text-[0.625rem] font-bold flex items-center justify-center">
                  {activeFilterCount}
                </span>
              )}
            </button>
            {hasFilters && (
              <button
                onClick={clearFilters}
                className="px-3 py-2 text-text-muted hover:text-danger border border-border hover:border-danger rounded-lg transition-colors text-sm flex items-center gap-1.5 whitespace-nowrap"
              >
                <X size={14} />
                Clear
              </button>
            )}
          </div>
        </div>

        {showFilters && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 pt-2 border-t border-border">
            <div>
              <label className="block text-xs font-medium text-text-muted mb-1">Category</label>
              <select
                value={categoryId}
                onChange={(e) => setCategoryId(e.target.value)}
                className="w-full px-3 py-2 bg-background border border-border rounded-lg text-sm text-text focus:outline-none focus:ring-2 focus:ring-primary/50"
              >
                <option value="">All Categories</option>
                {categories.map((category) => (
                  <option key={category.id} value={category.id}>
                    {category.name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-text-muted mb-1">Type</label>
              <select
                value={expenseType}
                onChange={(e) => setExpenseType(e.target.value)}
                className="w-full px-3 py-2 bg-background border border-border rounded-lg text-sm text-text focus:outline-none focus:ring-2 focus:ring-primary/50"
              >
                <option value="">All Types</option>
                <option value="ONE_TIME">One-time</option>
                <option value="RECURRING">Recurring</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-text-muted mb-1">Status</label>
              <select
                value={status}
                onChange={(e) => setStatus(e.target.value)}
                className="w-full px-3 py-2 bg-background border border-border rounded-lg text-sm text-text focus:outline-none focus:ring-2 focus:ring-primary/50"
              >
                <option value="">All Statuses</option>
                <option value="PAID">Paid</option>
                <option value="PENDING">Pending</option>
              </select>
            </div>
            <div className="sm:col-span-2 lg:col-span-1">
              <label className="block text-xs font-medium text-text-muted mb-1">Date Range</label>
              <div className="flex items-center gap-2">
                <DatePicker value={dateFrom} onChange={setDateFrom} />
                <span className="text-text-muted text-sm">-</span>
                <DatePicker value={dateTo} onChange={setDateTo} />
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Bulk actions */}
      {selectedIds.size > 0 && (
        <div className="bg-primary/5 border border-primary/20 rounded-xl p-3 flex flex-wrap items-center justify-between gap-3">
          <div className="text-sm text-text">
            <span className="font-medium">{selectedIds.size}</span> expense{selectedIds.size === 1 ? '' : 's'} selected
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setBulkConfirmOpen(true)}
              disabled={bulkDeleteExpenses.isPending}
              className="px-3 py-1.5 bg-danger text-white rounded-lg hover:bg-danger/90 transition-colors text-sm font-medium disabled:opacity-50 flex items-center gap-1.5"
            >
              {bulkDeleteExpenses.isPending ? (
                <Loader2 size={14} className="animate-spin" />
              ) : (
                <Trash2 size={14} />
              )}
              Delete
            </button>
            <button
              type="button"
              onClick={() => setSelectedIds(new Set())}
              disabled={bulkDeleteExpenses.isPending}
              className="px-3 py-1.5 text-sm text-text-muted hover:text-text border border-border rounded-lg transition-colors disabled:opacity-50 flex items-center gap-1.5"
            >
              <X size={14} />
              Clear selection
            </button>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="bg-surface rounded-xl border border-border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[900px]">
            <thead>
              <tr className="border-b border-border bg-background/50">
                <th className="px-4 py-3 w-10 text-left">
                  <input
                    ref={headerCheckboxRef}
                    type="checkbox"
                    checked={allSelected}
                    onChange={toggleAll}
                    disabled={expenses.length === 0}
                    title="Select all"
                    className="w-4 h-4 rounded border-border text-primary focus:ring-primary cursor-pointer disabled:cursor-default"
                  />
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase">Title</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase">Date</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase">Voucher</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase">Category</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase">Type</th>
                <th className="px-4 py-3 text-center text-xs font-medium text-text-muted uppercase">Status</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-text-muted uppercase">Amount</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-text-muted uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {isLoading ? (
                <tr>
                  <td colSpan={9} className="px-4 py-12 text-center">
                    <Loader2 className="w-8 h-8 animate-spin text-primary mx-auto" />
                  </td>
                </tr>
              ) : error ? (
                <tr>
                  <td colSpan={9} className="px-4 py-8 text-center text-danger">
                    <AlertCircle size={18} className="inline mr-2" /> Failed to load expenses
                  </td>
                </tr>
              ) : expenses.length === 0 ? (
                <tr>
                  <td colSpan={9} className="px-4 py-12 text-center text-text-muted">
                    {hasFilters ? (
                      <span>
                        No expenses match your filters.{' '}
                        <button onClick={clearFilters} className="text-primary hover:underline">
                          Clear filters
                        </button>
                      </span>
                    ) : (
                      <span className="inline-flex flex-col items-center gap-2">
                        <Receipt size={28} className="text-text-muted" />
                        No expenses recorded yet. Record your first daily or fixed expense.
                      </span>
                    )}
                  </td>
                </tr>
              ) : (
                expenses.map((expense) => (
                  <tr key={expense.id} className="hover:bg-background/50 transition-colors">
                    <td className="px-4 py-3">
                      <input
                        type="checkbox"
                        checked={selectedIds.has(expense.id)}
                        onChange={() => toggleOne(expense.id)}
                        className="w-4 h-4 rounded border-border text-primary focus:ring-primary cursor-pointer"
                      />
                    </td>
                    <td className="px-4 py-3">
                      <button
                        type="button"
                        onClick={() => setViewing(expense)}
                        className="font-medium text-text hover:text-primary transition-colors text-left"
                        title="View details"
                      >
                        {expense.title}
                      </button>
                      {(expense.vendor?.name || expense.vendorName) && (
                        <p className="text-xs text-text-muted">{expense.vendor?.name || expense.vendorName}</p>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm text-text whitespace-nowrap">{fmtDate(expense.expenseDate)}</td>
                    <td className="px-4 py-3 text-sm font-mono text-text-muted whitespace-nowrap">{expense.expenseNumber}</td>
                    <td className="px-4 py-3 text-sm text-text-muted">{expense.category?.name || 'Uncategorised'}</td>
                    <td className="px-4 py-3">
                      {expense.expenseType === 'RECURRING' ? (
                        <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-medium bg-primary/10 text-primary">
                          {FREQUENCY_LABELS[expense.frequency || ''] || 'Recurring'}
                        </span>
                      ) : (
                        <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-medium bg-background text-text-muted border border-border">
                          One-time
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span
                        className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
                          expense.status === 'PAID' ? 'bg-success/10 text-success' : 'bg-warning/10 text-warning'
                        }`}
                      >
                        {expense.status === 'PAID' ? <CheckCircle size={12} /> : <Clock size={12} />}
                        {expense.status === 'PAID' ? 'Paid' : 'Pending'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm font-semibold text-text text-right whitespace-nowrap">{fmt(expense.amount)}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1">
                        {expense.status === 'PENDING' && (
                          <button
                            type="button"
                            onClick={() => openPay(expense)}
                            className="p-1.5 rounded-lg hover:bg-background text-text-muted hover:text-success transition-colors"
                            title="Mark as paid"
                          >
                            <CheckCircle size={16} />
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={() => handlePrint(expense)}
                          disabled={voucher.isPending}
                          className="p-1.5 rounded-lg hover:bg-background text-text-muted hover:text-primary transition-colors disabled:opacity-50"
                          title="Print voucher"
                        >
                          <Printer size={16} />
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDownload(expense)}
                          disabled={voucher.isPending}
                          className="p-1.5 rounded-lg hover:bg-background text-text-muted hover:text-primary transition-colors disabled:opacity-50"
                          title="Download voucher"
                        >
                          <Download size={16} />
                        </button>
                        <button
                          type="button"
                          onClick={() => setViewing(expense)}
                          className="p-1.5 rounded-lg hover:bg-background text-text-muted hover:text-primary transition-colors"
                          title="View details"
                        >
                          <Eye size={16} />
                        </button>
                        <button
                          type="button"
                          onClick={() => openEdit(expense)}
                          className="p-1.5 rounded-lg hover:bg-background text-text-muted hover:text-primary transition-colors"
                          title="Edit"
                        >
                          <Pencil size={16} />
                        </button>
                        <button
                          type="button"
                          onClick={() => setConfirm(expense)}
                          className="p-1.5 rounded-lg hover:bg-background text-text-muted hover:text-danger transition-colors"
                          title="Delete"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <Pagination
          page={page}
          totalPages={meta?.totalPages || 1}
          total={meta?.total || 0}
          limit={limit}
          onPageChange={setPage}
          itemName="expenses"
        />
      </div>

      {/* Pay modal */}
      {paying && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4" role="dialog" aria-modal="true">
          <div className="bg-surface border border-border rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-5 border-b border-border bg-background/50 rounded-t-2xl">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-success/10 flex items-center justify-center">
                  <CheckCircle size={20} className="text-success" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-text">Mark Expense as Paid</h2>
                  <p className="text-xs text-text-muted">{paying.expenseNumber} — {paying.title}</p>
                </div>
              </div>
              <button type="button" onClick={() => setPaying(null)} className="p-2 hover:bg-background rounded-xl transition-colors">
                <X size={18} className="text-text-muted" />
              </button>
            </div>
            <div className="p-5 space-y-4">
              {payError && (
                <div className="flex items-start gap-2 p-3 bg-danger/10 border border-danger/20 rounded-lg text-sm text-danger">
                  <AlertCircle size={16} className="shrink-0 mt-0.5" />
                  <span>{payError}</span>
                </div>
              )}
              <div className="rounded-lg bg-background border border-border p-3 text-sm flex items-center justify-between">
                <span className="text-text-muted">Amount due</span>
                <span className="font-semibold text-text">{fmt(paying.amount)}</span>
              </div>
              <div>
                <label className="block text-sm font-medium text-text mb-1">Payment Method</label>
                <select
                  value={payForm.paymentMethod}
                  onChange={(e) => setPayForm({ ...payForm, paymentMethod: e.target.value })}
                  className="w-full px-3 py-2 border border-border rounded-lg bg-background text-text text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                >
                  {PAYMENT_METHODS.map((method) => (
                    <option key={method.value} value={method.value}>
                      {method.label}
                    </option>
                  ))}
                </select>
                <p className="text-xs text-text-muted mt-1">Cash payments also record a cashbook cash-out entry.</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-text mb-1">Payment Date *</label>
                <DatePicker
                  value={payForm.paymentDate}
                  onChange={(value) => setPayForm({ ...payForm, paymentDate: value })}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-text mb-1">Reference</label>
                <input
                  type="text"
                  value={payForm.paymentReference}
                  onChange={(e) => setPayForm({ ...payForm, paymentReference: e.target.value })}
                  placeholder="Cheque / transaction no."
                  className="w-full px-3 py-2 border border-border rounded-lg bg-background text-text text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>
              <div className="flex items-center justify-end gap-3 pt-2 border-t border-border">
                <button
                  type="button"
                  onClick={() => setPaying(null)}
                  disabled={payExpense.isPending}
                  className="px-4 py-2 text-sm text-text-muted hover:text-text transition-colors disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handlePay}
                  disabled={payExpense.isPending}
                  className="px-4 py-2 bg-success text-white rounded-lg hover:bg-success/90 transition-colors text-sm font-medium flex items-center gap-2 disabled:opacity-50"
                >
                  {payExpense.isPending ? <Loader2 size={16} className="animate-spin" /> : <CheckCircle size={16} />}
                  Confirm Payment
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <ExpenseDetailsModal
        open={viewing !== null}
        expense={viewing}
        onClose={() => setViewing(null)}
        onEdit={openEdit}
        onPrint={handlePrint}
      />

      <ConfirmDialog
        open={confirm !== null}
        title="Delete expense"
        description={
          <>
            Delete <strong>{confirm?.expenseNumber}</strong> — {confirm?.title}? This cannot be undone.
          </>
        }
        loading={deleteExpense.isPending}
        onConfirm={handleDelete}
        onCancel={() => setConfirm(null)}
      />

      <ConfirmDialog
        open={bulkConfirmOpen}
        title={`Delete ${selectedIds.size} expense${selectedIds.size === 1 ? '' : 's'}?`}
        description="This will remove the selected expenses. This cannot be undone."
        confirmLabel={`Delete ${selectedIds.size} expense${selectedIds.size === 1 ? '' : 's'}`}
        loading={bulkDeleteExpenses.isPending}
        onConfirm={handleConfirmBulkDelete}
        onCancel={() => setBulkConfirmOpen(false)}
      />
    </ExpensesLayout>
  );
}
